<template>
  <div class="w-full max-w-sm mx-auto mt-16 mb-16">
    <div class="text-center mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-1">eRegistry Admin Portal</h2>
      <p class="text-sm text-gray-500">Sign in to your account</p>
    </div>
    <!-- Session Conflict Modal -->
    <AppModal v-model="showConfirm" title="Active Session Detected" size="xl">
      <div class="space-y-4">
        <p class="text-sm text-gray-700 leading-relaxed">
          You are currently signed in on another device. For security purposes,
          we recommend having only one active session at a time.
        </p>
        <p class="text-sm text-gray-600">
          Would you like to log out of the other session and continue here?
        </p>
      </div>
      <template #footer>
        <div class="flex gap-3 justify-end">
          <button
            @click="cancelConfirm"
            class="text-xs px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold cursor-pointer">
            Cancel
          </button>
          <button
            @click="yesConfirm"
            class="text-xs px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition font-semibold cursor-pointer">
            <i class="fa fa-check-circle mr-1"></i>
            Logout Old Session
          </button>
        </div>
      </template>
    </AppModal>

    <!-- Main Login Card -->
    <div class="bg-white rounded-xl shadow-md border border-gray-100 p-6 sm:p-8">

        <!-- Login Form -->
        <form v-if="isOnLogin" @submit.prevent="onLoginSubmit" class="space-y-4">
          <!-- Username Input -->
          <div>
            <label for="username" class="block text-sm font-medium text-gray-700 mb-1.5">
              Username
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fa-solid fa-user text-gray-400 text-sm"></i>
              </div>
              <input
                  id="username"
                  v-model="form.usernameOrEmail"
                  type="text"
                  required
                  placeholder="Enter your username"
                  class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary cursor-text"
              />
            </div>
          </div>

          <!-- Password Input -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1.5">
              Password
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fa-solid fa-lock text-gray-400 text-sm"></i>
              </div>
              <input
                id="password"
                v-model="form.password"
                :type="show1 ? 'text' : 'password'"
                required
                placeholder="Enter your password"
                class="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary cursor-text"
              />
              <button
                type="button"
                @click="show1 = !show1"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i :class="show1 ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'" class="text-sm"></i>
              </button>
            </div>
          </div>

          <!-- Forgot Password Link -->
          <div class="flex items-center justify-end">
            <router-link
              :to="{name:'reset-password'}"
              class="text-sm font-medium text-primary hover:text-primary-700 hover:underline cursor-pointer">
              Forgot your password?
            </router-link>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="showSpinner"
            class="w-full py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-700 transition font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <template v-if="!showSpinner">
              <i class="fa-solid fa-right-to-bracket"></i>
              Sign In
            </template>
            <template v-else>
              <i class="fa-solid fa-spinner fa-spin"></i>
              Signing in...
            </template>
          </button>
        </form>

        <!-- OTP Verification -->
        <div v-else class="space-y-5">
          <p class="text-sm text-gray-600 text-center">
            Enter the verification code sent to your device
          </p>

          <Otp
            ref="otpInputRef"
            :input-number="otpSize"
            class="mx-auto"
            @otp-data="getOtpCode"
          />

          <div v-if="errorMessage == null">
            <button
              type="button"
              @click="onOtpSubmit"
              :disabled="showSpinner"
              class="w-full py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-700 transition font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="!showSpinner">Verify Code</span>
              <span v-else class="flex items-center justify-center">
                <i class="fa-solid fa-spinner fa-spin mr-2"></i>
                Verifying...
              </span>
            </button>
          </div>

          <div v-else>
            <button
              type="button"
              @click="onResendOtp"
              :disabled="showSpinner"
              class="w-full py-2.5 bg-white text-red-600 text-sm border border-red-300 rounded-lg hover:bg-red-50 transition font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="!showSpinner">Resend Code</span>
              <span v-else class="flex items-center justify-center">
                <i class="fa-solid fa-spinner fa-spin mr-2"></i>
                Resending...
              </span>
            </button>
          </div>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import AppModal from "@/components/ui/AppModal.vue";
import { useLoginStore } from "@/stores/login";
import jwtService from "@/app/services/jwt.service";
import Otp from "@/domains/auth/Login/otp/Otp.vue";
import { useToast } from "@/composables";
import { persistRefreshTokenExpiryTime } from "@/composables/tokenRefresh";

// Types
interface LoginForm {
  usernameOrEmail: string | null
  password: string | null
}

interface OtpPayload {
  token: string
  otp: string
}

interface Permission {
  action: string
  resource: string
}

// Router
const router = useRouter()

// Stores and Composables
const loginStore = useLoginStore()
const { successToast, errorToast } = useToast()

// Store state
const { getLoginData, getLoginError, getOtpSuccess, getUserLogoutSuccess } = storeToRefs(loginStore)

// Component refs
const otpInputRef = ref<InstanceType<typeof Otp> | null>(null)

// Reactive state
const showConfirm = ref<boolean>(false)
const isOnLogin = ref<boolean>(true)
const show1 = ref<boolean>(false)
const showSpinner = ref<boolean>(false)
const otpSize = ref<number>(4)
const errorMessage = ref<string | null>(null)
const otp = ref<string | null>(null)

const form = ref<LoginForm>({
  usernameOrEmail: null,
  password: null,
})

// Lifecycle
onMounted(() => {
  if (sessionStorage.getItem("access_token")) {
    const r = router.resolve({
      name: "user_profile",
    })
    window.location.assign(r.href)
  }
})

// Methods
const onLoginSubmit = () => {
  errorMessage.value = null
  showSpinner.value = true
  loginStore.doLoginUser(form.value)
      .finally(() => {
        showSpinner.value = false
      })
}

const onOtpSubmit = () => {
  if (showSpinner.value) return
  errorMessage.value = null
  showSpinner.value = true
  if (otp.value) {
    const payload: OtpPayload = {
      token: getLoginData.value.data,
      otp: otp.value
    }
    loginStore.doOtpVerification(payload)
  } else {
    errorToast("Error", "No OTP entered")
    showSpinner.value = false
  }
}

const onResendOtp = () => {
  otp.value = null
  errorMessage.value = null
  otpInputRef.value?.flush()
  loginStore.doLoginUser(form.value)
}

const yesConfirm = () => {
  showSpinner.value = true
  loginStore.doLogoutUser(form.value)
  showConfirm.value = false
}

const cancelConfirm = () => {
  showConfirm.value = false
}

const getOtpCode = (code: string) => {
  otp.value = code
  // Auto-verify as soon as all digits are entered (same state where the
  // Verify button is shown; after a failure the user must resend instead).
  if (errorMessage.value == null) {
    onOtpSubmit()
  }
}

// Watchers
watch(getLoginData, (data) => {
  if (data) {
    successToast("Success", data.message || "Success")
    showSpinner.value = false
    isOnLogin.value = false
  }
})

watch(getOtpSuccess, (loginObject) => {
  if (loginObject) {
    jwtService.saveToken(loginObject.accessToken)
    jwtService.saveUser(loginObject.user)

    if (loginObject.refreshToken) {
      sessionStorage.setItem('tokenRefresh', loginObject.refreshToken)
    }

    persistRefreshTokenExpiryTime(
      loginObject.refreshTokenExpiryTime ?? loginObject.user?.refreshTokenExpiryTime,
    )

    successToast("Welcome", loginObject.user.givenName)

    const permissions: Permission[] = []
    loginObject.user.roles.forEach((role: any) => {
      role.permissions.forEach((permission: any) => {
        permissions.push({
          action: permission.permission,
          resource: "general"
        })
      })
    })

    sessionStorage.setItem('abilities', JSON.stringify(permissions))
    showSpinner.value = false

    const r = router.resolve({
      name: "Home",
    })
    window.location.assign(r.href)
  }
})

watch(getUserLogoutSuccess, (data) => {
  if (data) {
    showSpinner.value = true
    successToast("Logout", "Logged out old session")
    loginStore.doLoginUser(form.value)
  }
})

watch(getLoginError, (data) => {
  if (data) {
    showSpinner.value = false
    if (data.status === 409) {
      showConfirm.value = true
    } else {
      errorToast("Error", data.message)
      errorMessage.value = data.message
    }
  }
})
</script>
